import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function BackupRestore() {
  const { user } = useAuth();
  const [backupFiles, setBackupFiles] = useState([]);
  const [backupLogs, setBackupLogs]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [creating, setCreating]       = useState(false);
  const [restoring, setRestoring]     = useState(false);
  const [uploadFile, setUploadFile]   = useState(null);
  const [downloading, setDownloading] = useState(null); // filename being downloaded

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/backup/list');
      setBackupFiles(res.data.files || []);
      setBackupLogs(res.data.logs   || []);
    } catch (err) {
      console.error('backup list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  // Merge filesystem file list with log entries so we get type/status/date/size
  const mergedFiles = backupFiles.map(f => {
    const log = backupLogs.find(l => l.filename === f.filename);
    return {
      filename:     f.filename,
      file_size_kb: f.file_size_kb || log?.file_size_kb || null,
      backup_type:  log?.backup_type || 'manual',
      status:       log?.status      || 'success',
      created_at:   log?.created_at  || null,
      created_by:   log?.created_by_name || '—',
    };
  });

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/api/backup/create');
      await load();
      alert('Backup created successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Backup failed');
    } finally {
      setCreating(false);
    }
  };

  // Download via axios (sends JWT header) then trigger browser save
  const handleDownload = async (filename) => {
    setDownloading(filename);
    try {
      const res = await api.get(`/api/backup/download/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDownloading(null);
    }
  };

  const handleRestore = async () => {
    if (!uploadFile) { alert('Please select a .sql file first'); return; }
    if (!window.confirm('WARNING: This will overwrite ALL existing data with the backup file. This action cannot be undone. Are you absolutely sure?')) return;
    if (!window.confirm('Last confirmation: Proceed with restore? ALL current data will be lost.')) return;
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('backup_file', uploadFile);
      await api.post('/api/backup/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Restore completed successfully! Please verify the application data.');
      setUploadFile(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const fmtSize = (kb) => {
    if (!kb) return '—';
    if (kb >= 1024) return (kb / 1024).toFixed(1) + ' MB';
    return kb + ' KB';
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (!user || user.role !== 'admin') return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center bg-red-50 border border-red-200 rounded-xl p-8">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-xl font-bold text-red-800">Access Denied</h2>
        <p className="text-red-600 mt-2">Only administrators can access backup & restore.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Backup & Restore</h1>
        <p className="text-gray-500 text-sm mt-1">Manage database backups</p>
      </div>

      {/* Warning Box */}
      <div className="bg-red-50 border border-red-300 rounded-xl p-5 flex gap-4">
        <div className="text-3xl">⚠️</div>
        <div>
          <h3 className="font-bold text-red-800 text-lg">Caution — Destructive Operations</h3>
          <p className="text-red-700 mt-1 text-sm">
            <strong>Restore will permanently overwrite ALL data</strong> in the system with the contents of the backup file.
            This includes all visitors, employees, vehicles, trips, and other records.{' '}
            <strong>This action cannot be undone.</strong> Always create a fresh backup before restoring.
          </p>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create Backup */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">💾</div>
            <div>
              <h2 className="font-bold text-gray-800 text-lg">Create Backup</h2>
              <p className="text-sm text-gray-500">Export current database snapshot</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            Creates a full SQL dump of the current database. The backup file will be saved on the server and available for download below.
          </p>
          <button onClick={handleCreate} disabled={creating}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
            {creating ? <><span className="animate-spin">⏳</span> Creating backup...</> : <>💾 Create Backup Now</>}
          </button>
        </div>

        {/* Restore */}
        <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-2xl">🔄</div>
            <div>
              <h2 className="font-bold text-gray-800 text-lg">Restore from File</h2>
              <p className="text-sm text-gray-500">Overwrite database from backup</p>
            </div>
          </div>
          <p className="text-sm text-red-600 mb-4 font-medium">
            ⚠️ This will destroy all current data and replace it with the backup.
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Backup File (.sql)</label>
            <input type="file" accept=".sql"
              onChange={e => setUploadFile(e.target.files[0])}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 file:text-sm" />
            {uploadFile && <p className="text-xs text-gray-500 mt-1">Selected: {uploadFile.name}</p>}
          </div>
          <button onClick={handleRestore} disabled={restoring || !uploadFile}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
            {restoring ? <><span className="animate-spin">⏳</span> Restoring... please wait</> : <>🔄 Restore Database</>}
          </button>
        </div>
      </div>

      {/* Available Backups Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Available Backups</h2>
          <button onClick={load} className="text-sm text-blue-600 hover:text-blue-800">🔄 Refresh</button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">💾</div>
            <p>Loading backups...</p>
          </div>
        ) : mergedFiles.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">💾</div>
            <p>No backups found. Create your first backup above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Filename','Type','Size','Status','Created','Download'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {mergedFiles.map((b, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-gray-800 text-xs max-w-xs truncate" title={b.filename}>{b.filename}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{b.backup_type}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtSize(b.file_size_kb)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDownload(b.filename)}
                        disabled={downloading === b.filename}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-50">
                        {downloading === b.filename ? '⏳ Downloading...' : '📥 Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Backup Logs */}
      {backupLogs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Backup History Log</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Filename','Type','Size','Status','Created','By'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {backupLogs.map((b, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-gray-700 text-xs max-w-xs truncate" title={b.filename}>{b.filename}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">{b.backup_type || 'manual'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fmtSize(b.file_size_kb)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{b.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
